import moment from 'moment'
import { useTheme } from '@material-ui/core'
import { Event as CalendarBadgeIcon } from '@material-ui/icons'
import { ListItem, Text, Button } from 'shared/ui'
import { eventTypeLabel } from './eventTypes'
import { eventOwnerColor } from './eventStatus'
import { useLinkedRecordName } from './linkResolver'
import { useEventLogActivity, hasEventStarted } from './useEventLogActivity'
import { hasLoggedActivity, isEventCanceled } from './eventActivity'

// One event in the Daily Event sidebar (and any future flat list). Left accent tinted by
// the linked Calendar's owner (hashed, same colors as their own avatar — see
// eventOwnerColor), name as primary, "<linked> · <type> · <assignee>" as the meta line,
// and the start–end time right-aligned via ListItem's own label slot.
const linkedLabel = (link, resolvedName) => {
  if (!link) return null
  return resolvedName || link.target_object_data?.name || link.title || link.target_object_type?.split('/')?.pop()
}

const assigneeLabel = (event) => {
  const u = event?.assigned_to
  if (!u) return null
  return `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || null
}

const timeLabel = (event) => {
  if (!event?.starts_at) return ''
  const start = moment(event.starts_at).format('HH:mm')
  return event.ends_at ? `${start}–${moment(event.ends_at).format('HH:mm')}` : start
}

const calendarOwnerLabel = (event) => {
  const o = event?.calendar?.owner
  if (!o) return null
  return `${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email || null
}

function EventRow({ event, onClick }) {
  const theme = useTheme()
  const { logActivity, isFetching: isLoggingActivity } = useEventLogActivity()
  const isCanceled = isEventCanceled(event)
  // started, not canceled, and nothing logged against it yet
  const canLogActivity = hasEventStarted(event) && !isCanceled && !hasLoggedActivity(event)
  // Old links can carry no target_object_data — the hook fetches the record's name then.
  const link = (event?.links || [])[0]
  const resolvedName = useLinkedRecordName(link)
  const meta = [linkedLabel(link, resolvedName), eventTypeLabel(event?.event_type), assigneeLabel(event)]
    .filter(Boolean)
    .join(' · ')
  const calendarOwner = calendarOwnerLabel(event)

  // shared/ui ListItem (default "button" variant) renders through react-router's <Link>
  // with `to={undefined}`; after our onClick runs, the Link still fires navigate() →
  // history.push(undefined), which wipes the query string we just set to open the event
  // sidebar. preventDefault stops that second navigation (the "card" variant already
  // does this internally; the default one doesn't).
  const handleClick = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation() }
    onClick && onClick()
  }

  return (
    <ListItem
      noGutter
      onClick={handleClick}
      label={<Text variant="body2" color="textMuted" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{timeLabel(event)}</Text>}
      primary={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 3, alignSelf: 'stretch', minHeight: 16, borderRadius: 2, background: eventOwnerColor(event) }} />
          <Text style={{ fontWeight: 500, ...(isCanceled ? { color: theme.palette.grey.muted, textDecoration: 'line-through' } : {}) }}>
            {event?.name}
          </Text>
        </div>
      }
      secondary={
        (meta || calendarOwner || canLogActivity) ? (
          <>
            {meta && <span style={{ display: 'block' }}>{meta}</span>}
            {calendarOwner &&
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                <CalendarBadgeIcon style={{ fontSize: 13 }} />
                {calendarOwner}
              </span>
            }
            {/* Once the event has started — same "Log Activity" the event's detail sidebar
                ends with. Its own click target: the row itself opens the sidebar. */}
            {canLogActivity &&
              <Button
                variant="text"
                color="primary"
                size="small"
                label="Log activity"
                loading={isLoggingActivity}
                disabled={isLoggingActivity}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); logActivity(event) }}
                style={{ marginTop: 2, paddingLeft: 0 }}
              />
            }
          </>
        ) : undefined
      }
    />
  )
}

export default EventRow
